import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getMoods, saveMood, getTodayMood } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

const MOOD_OPTIONS = [
  { score: 1, label: 'Very low', emoji: '😔' },
  { score: 2, label: 'Low', emoji: '😕' },
  { score: 3, label: 'Okay', emoji: '😐' },
  { score: 4, label: 'Good', emoji: '🙂' },
  { score: 5, label: 'Very good', emoji: '😊' },
];

// Simple date formatter for chart labels
const formatDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
};

const WellbeingTrendChart = () => {
  const { user } = useAuth();
  const { isReadOnly, readonlyToken } = useReadOnly();
  const [moods, setMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  
  // Mood check-in state
  const [selectedMood, setSelectedMood] = useState(null);
  const [savingMood, setSavingMood] = useState(false);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [moodLoading, setMoodLoading] = useState(true);

  // Get user's join date (account creation) as YYYY-MM-DD
  const userJoinDate = useMemo(() => {
    if (user?.created_at) {
      const date = new Date(user.created_at);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return null;
  }, [user?.created_at]);

  // Load today's mood on mount or when readonly mode changes
  useEffect(() => {
    const loadTodayMood = async () => {
      try {
        const todayMood = await getTodayMood();
        if (todayMood) {
          setSelectedMood(todayMood.score);
          setHasCheckedInToday(true);
        } else {
          setSelectedMood(null);
          setHasCheckedInToday(false);
        }
      } catch (error) {
        console.error('Error loading today mood:', error);
      } finally {
        setMoodLoading(false);
      }
    };
    loadTodayMood();
  }, [readonlyToken]);

  // Load mood history
  useEffect(() => {
    fetchMoods();
  }, [timeRange, readonlyToken]);

  const fetchMoods = async () => {
    try {
      setLoading(true);
      const data = await getMoods(timeRange);
      setMoods(data);
    } catch (error) {
      console.error('Error fetching moods:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle mood selection
  const handleMoodSelect = useCallback(async (score) => {
    if (savingMood || isReadOnly) return;
    
    setSavingMood(true);
    try {
      await saveMood(score);
      setSelectedMood(score);
      setHasCheckedInToday(true);
      
      toast.success(
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4" />
          <span>Thanks for sharing.</span>
        </div>,
        { duration: 3500 }
      );
      
      // Refresh mood data
      await fetchMoods();
    } catch (error) {
      console.error('Error saving mood:', error);
    } finally {
      setSavingMood(false);
    }
  }, [savingMood, isReadOnly]);

  // Get emoji for tooltip
  const getMoodEmoji = (score) => {
    const emojis = { 1: '😔', 2: '😕', 3: '😐', 4: '🙂', 5: '😊' };
    return emojis[score] || '😐';
  };

  // Create mood map for quick lookup
  const moodMap = useMemo(() => {
    const map = {};
    moods.forEach(mood => {
      map[mood.date] = mood;
    });
    return map;
  }, [moods]);

  // Get today's date as YYYY-MM-DD
  const getTodayStr = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate chart data starting from join date
  const chartData = useMemo(() => {
    if (!userJoinDate) return [];
    
    const dates = [];
    const todayStr = getTodayStr();
    
    // Parse join date
    const [joinYear, joinMonth, joinDay] = userJoinDate.split('-').map(Number);
    const joinDate = new Date(joinYear, joinMonth - 1, joinDay);
    
    // Parse today
    const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
    const today = new Date(todayYear, todayMonth - 1, todayDay);
    
    // Calculate days since join
    const diffTime = today.getTime() - joinDate.getTime();
    const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    // Limit to selected time range
    const daysToShow = Math.min(timeRange, totalDays);
    
    // Generate dates from (today - daysToShow + 1) to today
    // But never before join date
    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Skip if before join date
      if (dateStr < userJoinDate) continue;
      
      const moodEntry = moodMap[dateStr];
      dates.push({
        date: dateStr,
        score: moodEntry?.score || null
      });
    }
    
    return dates;
  }, [moodMap, timeRange, userJoinDate]);

  // Generate date labels for x-axis
  const dateLabels = useMemo(() => {
    if (chartData.length === 0) return [];
    
    const totalDays = chartData.length;
    
    // For small ranges, show first and last
    if (totalDays <= 3) {
      return [
        { index: 0, label: formatDateLabel(chartData[0]?.date) },
        { index: totalDays - 1, label: formatDateLabel(chartData[totalDays - 1]?.date) }
      ].filter((l, i, arr) => i === 0 || l.index !== arr[0].index);
    }
    
    // For larger ranges, show ~4-5 evenly spaced labels
    const labels = [];
    const numLabels = Math.min(5, totalDays);
    const step = Math.max(1, Math.floor((totalDays - 1) / (numLabels - 1)));
    
    for (let i = 0; i < totalDays; i += step) {
      if (chartData[i]) {
        labels.push({ index: i, label: formatDateLabel(chartData[i].date) });
      }
    }
    
    // Always include last date
    if (labels.length > 0 && labels[labels.length - 1].index !== totalDays - 1) {
      labels.push({ index: totalDays - 1, label: formatDateLabel(chartData[totalDays - 1].date) });
    }
    
    return labels;
  }, [chartData]);

  // Fixed 5-level bar height: score 1=20%, 2=40%, 3=60%, 4=80%, 5=100%
  const getBarHeight = (score) => {
    if (!score) return 0;
    return score * 20;
  };

  // Calm, cohesive color palette - sage/teal/slate family
  // All bars use the same muted teal color for visual cohesion
  const BAR_COLOR = '#6b9b9b'; // Muted sage-teal
  const LINE_COLOR = '#94b8b8'; // Lighter shade for the trend line
  
  const getBarColor = (score) => {
    if (!score) return 'transparent';
    return BAR_COLOR;
  };

  // Loading state
  if (loading && moodLoading) {
    return (
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
        <div className="animate-pulse">
          <div className="h-4 w-40 bg-slate-200 rounded mb-3"></div>
          <div className="flex justify-center gap-2 mb-3">
            {[1,2,3,4,5].map(i => <div key={i} className="w-9 h-9 bg-slate-200 rounded-full"></div>)}
          </div>
          <div className="h-24 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Check if there's any mood data
  const hasAnyMoodData = moods.length > 0;

  return (
    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
      {/* 1. Title */}
      <h3 className="text-base font-semibold text-slate-800 mb-2 text-center">
        How You've Been Feeling
      </h3>
      
      {/* 2. Mood Faces */}
      {!moodLoading && (
        <div className="flex justify-center gap-1.5 mb-2">
          {MOOD_OPTIONS.map((mood) => {
            const isSelected = selectedMood === mood.score;
            return (
              <button
                key={mood.score}
                onClick={() => handleMoodSelect(mood.score)}
                disabled={savingMood}
                className={`
                  w-9 h-9 rounded-full flex items-center justify-center
                  bg-white border border-slate-200
                  transition-all duration-150
                  ${isSelected ? 'ring-2 ring-primary ring-offset-1 bg-primary/10 border-primary/30' : ''}
                  ${savingMood ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110 hover:bg-slate-50 hover:border-slate-300'}
                `}
                title={mood.label}
                aria-label={mood.label}
              >
                <span className="text-[22px]">{mood.emoji}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* 3. Date Range Buttons */}
      <div className="flex justify-center mb-3">
        <div className="flex gap-1 bg-white rounded-lg p-1 border border-slate-200">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              onClick={() => setTimeRange(days)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                timeRange === days
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* 4. Chart or Empty State */}
      {!hasAnyMoodData ? (
        <div className="text-center py-4">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <span className="text-xl">🌱</span>
          </div>
          <p className="text-slate-500 text-sm">
            Tap an emoji above to record how you're feeling.
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-[10px] text-slate-400">
            <span>Higher</span>
            <span>Lower</span>
          </div>

          {/* Chart Area */}
          <div className="ml-12">
            {/* Background grid - subtle */}
            <div className="absolute left-12 right-0 h-20 flex flex-col justify-between pointer-events-none">
              {[1, 2, 3, 4, 5].map((level) => (
                <div key={level} className="border-b border-slate-100" style={{ height: '20%' }} />
              ))}
            </div>
            
            {/* Line graph with dots */}
            <div className="relative h-20">
              {/* SVG Line Graph */}
              {(() => {
                // Get only data points with scores
                const dataPoints = chartData
                  .map((item, idx) => ({ ...item, idx }))
                  .filter(item => item.score !== null);
                
                if (dataPoints.length < 1) return null;
                
                const totalDays = chartData.length;
                
                // Calculate points
                const points = dataPoints.map((point) => {
                  const x = ((point.idx + 0.5) / totalDays) * 100;
                  // Score 1 = 90% from top, Score 5 = 10% from top
                  const y = 100 - (point.score * 18) - 5;
                  return { x, y, score: point.score, date: point.date };
                });
                
                // Create line path
                const pathD = points.length > 1 
                  ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                  : null;
                
                return (
                  <>
                    {/* Main SVG for the line */}
                    <svg 
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      {/* Connecting line - soft sage color */}
                      {pathD && (
                        <path
                          d={pathD}
                          fill="none"
                          stroke="#7fb3b3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      )}
                    </svg>
                    {/* Separate SVG for dots - preserves aspect ratio for round dots */}
                    <svg 
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{ overflow: 'visible' }}
                    >
                      {points.map((p, i) => {
                        // Calculate actual pixel position
                        const xPercent = p.x;
                        const yPercent = p.y;
                        return (
                          <circle
                            key={i}
                            cx={`${xPercent}%`}
                            cy={`${yPercent}%`}
                            r="3"
                            fill="#5a9a9a"
                          />
                        );
                      })}
                    </svg>
                  </>
                );
              })()}
              
              {/* Hover areas for tooltips */}
              <div className="absolute inset-0 flex">
                {chartData.map((item, idx) => {
                  const hasData = item.score !== null;
                  return (
                    <div
                      key={item.date}
                      className="flex-1 group relative"
                    >
                      {hasData && (
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                          <div className="bg-slate-600 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap shadow-lg text-center mt-[-36px]">
                            <span className="text-base">{getMoodEmoji(item.score)}</span>
                            <div className="text-[10px] text-slate-300 mt-0.5">
                              {formatDateLabel(item.date)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* X-axis date labels */}
            <div className="flex justify-between mt-1 text-[10px] text-slate-400">
              {dateLabels.map((item, index) => (
                <span key={index}>{item.label}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 5. Footer */}
      <p className="text-[10px] text-slate-400 mt-2 text-center">
        This is not a medical assessment. There's no right or wrong — just patterns.
      </p>
    </div>
  );
};

export default WellbeingTrendChart;
